import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { MeasurementModeName } from "../../_objects/measurement-mode-name";
import {
  CreatePdfBodyRequest,
  InspectionReport,
  InspectionReportList,
  InspectionReportMetaData,
  InspectionReportNew,
  InspectionReportPdfUrl,
  InspectionReportReuse,
  InspectionReportUpdate,
  InspectionReportUpdateLocations,
  InspectionReportUpdateTexts,
  InspectionReportUri,
  PastReportVersion,
  PdfOptions,
  ReportStatus
} from "../../_objects/inspection-report";
import { HttpService } from "../http.service";
import { InspectionReportContractService } from "../interfaces/inspection-report-contract.service";
import { SubjectFilter } from "../storage.service";
import { inspectionReportListToDomain } from "../mappers/inspection-report.mapper";
import { InspectionReportListResponse } from "../responses/inspection-report.response";

@Injectable({
  providedIn: "root"
})
export class InspectionReportOnlineService extends HttpService implements InspectionReportContractService {
  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router
  ) {
    super();
    super.setCookieService(cookieService);
    super.setHttpClient(http);
    super.setRouter(router);
  }

  // RP01 GET /api/v1/reports
  getInspectionReports(
    inspectionId?: string,
    statusCode?: string,
    subjectFilter?: SubjectFilter,
    isOwn?: boolean,
    sort?: { active: string; direction: string },
    page?: { index: number; size: number },
    searchText?: string
  ): Observable<InspectionReportList> {
    const endpoint = super.getEndpoint("reports");

    let httpParams = new HttpParams();
    if (page) {
      httpParams = httpParams.set("p", page.index.toString()).set("n", page.size.toString());
    }
    if (inspectionId) {
      httpParams = httpParams.set("ip", inspectionId);
    }
    if (subjectFilter && subjectFilter.subjectModelId) {
      httpParams = httpParams.set("md", subjectFilter.subjectModelId);
    }
    if (subjectFilter && subjectFilter.subjectId) {
      httpParams = httpParams.set("sb", subjectFilter.subjectId);
    }
    if (subjectFilter && subjectFilter.owner) {
      httpParams = httpParams.set("owner", subjectFilter.owner);
    }
    if (subjectFilter && subjectFilter.country) {
      httpParams = httpParams.set("country", subjectFilter.country);
    }
    if (subjectFilter && subjectFilter.state) {
      httpParams = httpParams.set("state", subjectFilter.state);
    }
    if (subjectFilter && subjectFilter.district) {
      httpParams = httpParams.set("district", subjectFilter.district);
    }
    if (subjectFilter && subjectFilter.site) {
      httpParams = httpParams.set("site", subjectFilter.site);
    }
    if (subjectFilter && subjectFilter.project) {
      httpParams = httpParams.set("project", subjectFilter.project);
    }
    if (isOwn !== null && isOwn !== undefined) {
      httpParams = httpParams.set("isOwn", String(isOwn));
    }
    if (statusCode) {
      httpParams = httpParams.set("st", statusCode);
    }
    if (sort) {
      httpParams = httpParams.set("sort", sort.active).set("sortOrder", sort.direction);
    }
    if (searchText) {
      httpParams = httpParams.set("q", searchText);
    }

    return this.http
      .get<InspectionReportListResponse>(endpoint, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => inspectionReportListToDomain(response)));
  }

  // RP02 GET /api/v1/reports/{reportId}
  getInspectionReport(reportId: string, version: number = null): Observable<InspectionReport> {
    const endpoint = super.getEndpoint("reports", reportId);

    let httpParams = new HttpParams();
    if (version && version > 0) {
      httpParams = httpParams.set("version", version.toString());
    }

    return this.http
      .get<InspectionReport>(endpoint, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as InspectionReport));
  }

  // RP03 POST /api/v1/reports
  createInspectionReport(inspectionReport: InspectionReportNew): Observable<InspectionReport> {
    const endpoint = super.getEndpoint("reports");

    return this.http
      .post<InspectionReport>(endpoint, inspectionReport, { withCredentials: super.getWithCredentialsOption(), params: new HttpParams() })
      .pipe(map((response) => response as InspectionReport));
  }

  // RP04 PUT /api/v1/reports/{reportId}
  updateInspectionReport(reportId: string, inspectionReportUpdate: InspectionReportUpdate): Observable<InspectionReport> {
    const endpoint = super.getEndpoint("reports", reportId);

    return this.http
      .put<InspectionReport>(endpoint, inspectionReportUpdate, { withCredentials: super.getWithCredentialsOption(), params: new HttpParams() })
      .pipe(map((response) => response as InspectionReport));
  }

  // RP05 DELETE /api/v1/reports/{reportId}
  deleteInspectionReport(reportId: string): Observable<any> {
    const endpoint = super.getEndpoint("reports", reportId);

    return this.http.delete(endpoint, { withCredentials: super.getWithCredentialsOption(), params: new HttpParams() });
  }

  // RP06 GET /api/v1/reports/related/inspections
  getInspectionReportsByInspectionIds(inspectionIds: string[], size: number, index?: number): Observable<InspectionReportList> {
    const endpoint = super.getEndpoint("reports", "related", "inspections");

    let httpParams = new HttpParams();
    if (inspectionIds) {
      inspectionIds.forEach((inspectionId) => {
        httpParams = httpParams.append("ip", inspectionId);
      });
    }
    if (size) {
      httpParams = httpParams.set("n", size.toString());
    }
    if (index) {
      httpParams = httpParams.set("p", index.toString());
    }

    return this.http
      .get<InspectionReportList>(endpoint, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as InspectionReportList));
  }

  // RP07 PUT /api/v1/reports/{reportId}/reportLocations
  patchInspectionReportLocations(inspectionReportUpdateLocations: InspectionReportUpdateLocations): Observable<any> {
    const endpoint = super.getEndpoint("reports", inspectionReportUpdateLocations.reportId, "reportLocations");

    return this.http
      .put<InspectionReportUpdateLocations>(endpoint, inspectionReportUpdateLocations, {
        withCredentials: super.getWithCredentialsOption(),
        params: new HttpParams()
      })
      .pipe(map((response) => response));
  }

  // RP08 PUT /api/v1/reports/{reportId}/editableTexts
  patchInspectionReportTexts(inspectionReportUpdateTexts: InspectionReportUpdateTexts): Observable<any> {
    const endpoint = super.getEndpoint("reports", inspectionReportUpdateTexts.reportId, "editableTexts");

    return this.http
      .put<InspectionReportUpdateTexts>(endpoint, inspectionReportUpdateTexts, {
        withCredentials: super.getWithCredentialsOption(),
        params: new HttpParams()
      })
      .pipe(map((response) => response));
  }

  // RP09 PUT /api/v1/reports/{reportId}/status
  patchInspectionReportStatus(reportStatus: ReportStatus, measurementModeName: MeasurementModeName): Observable<InspectionReport> {
    const endpoint = super.getEndpoint("reports", reportStatus.reportId, "status");

    const httpParams = this.buildMeasurementModeParams(measurementModeName);

    return this.http
      .put<InspectionReport>(endpoint, reportStatus, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response));
  }

  // RP10 PUT /api/v1/reports/{reportId}/editableTexts/import
  importInspectionReportTexts(inspectionReport: InspectionReport, file: File, fileType: string): Observable<InspectionReport> {
    const endpoint = super.getEndpoint("reports", inspectionReport.reportId, "editableTexts", "import");

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("type", fileType);

    return this.http.put<InspectionReport>(endpoint, formData, { withCredentials: super.getWithCredentialsOption() });
  }

  // RP11 GET /api/v1/reports/{reportId}/pdf
  getInspectionReportPdf(reportId: string, version?: number, preview?: boolean): Observable<Blob> {
    const endpoint = super.getEndpoint("reports", reportId, "pdf");

    let httpParams = new HttpParams();
    if (version) {
      httpParams = httpParams.set("version", String(version));
    }
    if (preview) {
      httpParams = httpParams.set("preview", String(preview));
    }

    return this.http.get(endpoint, {
      withCredentials: super.getWithCredentialsOption(),
      responseType: "blob",
      params: httpParams
    });
  }

  // RP12 GET /api/v1/reports/{reportId}/pdf/url
  getInspectionReportPdfUrl(reportId: string, version?: string, preview?: boolean, fileName?: string): Observable<InspectionReportPdfUrl> {
    return this.getInspectionReportPdfInline(reportId, version, preview, fileName).pipe(
      map((blob) => {
        const pdfUrl = new InspectionReportPdfUrl();
        pdfUrl.url = URL.createObjectURL(blob);
        return pdfUrl;
      })
    );
  }

  // RP21 GET /api/v1/reports/{reportId}/pdf/inline/fileName?
  getInspectionReportPdfInline(reportId: string, version?: string, preview?: boolean, fileName?: string): Observable<Blob> {
    const endpoint = fileName
      ? super.getEndpoint("reports", reportId, "pdf", "inline", fileName)
      : super.getEndpoint("reports", reportId, "pdf", "inline");

    let httpParams = new HttpParams();
    if (version) {
      httpParams = httpParams.set("version", version);
    }
    if (preview) {
      httpParams = httpParams.set("preview", String(preview));
    }

    return this.http.get(endpoint, {
      withCredentials: super.getWithCredentialsOption(),
      responseType: "blob",
      params: httpParams
    });
  }

  // RP13 POST /api/v1/reports/{reportId}/pdf
  createInspectionReportPdf(
    reportId: string,
    pdfOptions: PdfOptions,
    measurementModeName: MeasurementModeName,
    preview?: boolean
  ): Observable<InspectionReportPdfUrl> {
    const endpoint = super.getEndpoint("reports", reportId, "pdf");

    let httpParams = this.buildMeasurementModeParams(measurementModeName);

    if (preview) {
      httpParams = httpParams.set("preview", String(preview));
    }

    return this.http
      .post<InspectionReportPdfUrl>(endpoint, pdfOptions, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as InspectionReportPdfUrl));
  }

  putInspectionReportReuse(reuseRequest: InspectionReportReuse): Observable<InspectionReport> {
    const endpoint = super.getEndpoint("reports", reuseRequest.reportId, "editableTexts", "import", "reusedata");

    return this.http
      .put<InspectionReport>(endpoint, reuseRequest, { withCredentials: super.getWithCredentialsOption(), params: new HttpParams() })
      .pipe(map((response) => response as InspectionReport));
  }

  // RP15 GET /api/v1/reports/{reportId}/share
  getInspectionReportSharedLink(reportId: string, expiryDate: string, version?: number, isPreview?: boolean): Observable<InspectionReportUri> {
    const endpoint = super.getEndpoint("reports", reportId, "share");

    let httpParams = new HttpParams();
    if (expiryDate) {
      httpParams = httpParams.set("expiryDate", expiryDate.toString());
    }
    if (version) {
      httpParams = httpParams.set("version", version.toString());
    }
    if (isPreview) {
      httpParams = httpParams.set("isPreview", String(isPreview));
    }

    return this.http
      .get<InspectionReportUri>(endpoint, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as InspectionReportUri));
  }

  // RP015 POST /api/v1/reports/{reportId}
  createNewVersionInspectionReport(reportId: string): Observable<any> {
    const endpoint = super.getEndpoint("reports", reportId);

    return this.http
      .post<InspectionReport>(endpoint, reportId, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as InspectionReport));
  }

  // RP16 GET /api/v1/reports/{reportId}/history
  getInspectionReportVersion(reportId: string): Observable<PastReportVersion[]> {
    const endpoint = super.getEndpoint("reports", reportId, "history");

    return this.http
      .get<PastReportVersion[]>(endpoint, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as PastReportVersion[]));
  }

  // RP17 POST /api/v1/reports/{reportId}/pdf/async
  createReportPdfAsync(
    reportId: string,
    pdfOptions: PdfOptions,
    measurementModeName: MeasurementModeName,
    preview?: boolean,
    previewPdfHtml?: string
  ): Observable<InspectionReport> {
    const endpoint = super.getEndpoint("reports", reportId, "pdf", "async");

    let httpParams = this.buildMeasurementModeParams(measurementModeName);

    if (preview != undefined) {
      httpParams = httpParams.set("preview", String(preview));
    }

    const createPdfBodyRequest: CreatePdfBodyRequest = new CreatePdfBodyRequest();
    createPdfBodyRequest.pdfOptions = pdfOptions;
    if (previewPdfHtml) {
      createPdfBodyRequest.previewPdfHtml = previewPdfHtml;
    }

    return this.http
      .post<any>(endpoint, createPdfBodyRequest, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as any));
  }

  // RP18 POST api/reports/{reportId}/thumbnail
  createReportThumbnail(reportId: string, pdfOptions: PdfOptions, measurementModeName: MeasurementModeName): Observable<any> {
    const endpoint = super.getEndpoint("reports", reportId, "thumbnail");

    const httpParams = this.buildMeasurementModeParams(measurementModeName);

    return this.http
      .post<any>(endpoint, pdfOptions, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as any));
  }

  // RP19 PUT api/reports/{reportId}/thumbFilePath
  setUnavailableThumbFilePath(reportId: string): Observable<any> {
    const endpoint = super.getEndpoint("reports", reportId, "thumbFilePath");

    return this.http.put<any>(endpoint, { withCredentials: super.getWithCredentialsOption() }).pipe(map((response) => response as any));
  }

  // RP20 POST /api/reports/metadata
  getReportsMetaDataForInspections(inspectionIds: string[]): Observable<InspectionReportMetaData[]> {
    // Need to keep in order to respect the contract
    return null;
  }

  // RPH1 GET /api/v1/reports/{reportId}/preview
  getInspectionReportPreview(reportId: string, measurementModeName: MeasurementModeName, firstPageOnly: boolean): Observable<string> {
    const endpoint = super.getEndpoint("reports", reportId, "preview");

    let httpParams = this.buildMeasurementModeParams(measurementModeName);

    if (firstPageOnly) {
      httpParams = httpParams.set("firstPageOnly", true);
    }

    return this.http.get(endpoint, {
      withCredentials: super.getWithCredentialsOption(),
      params: httpParams,
      responseType: "text"
    });
  }

  private buildMeasurementModeParams(measurementModeName: MeasurementModeName): HttpParams {
    let httpParams = new HttpParams();

    if (!measurementModeName) return httpParams;

    httpParams = httpParams
      .set("objectDistance", measurementModeName.objectDistance)
      .set("distance", measurementModeName.distance)
      .set("pointToLine", measurementModeName.pointToLine)
      .set("depth", measurementModeName.depth)
      .set("lines", measurementModeName.lines)
      .set("area", measurementModeName.area)
      .set("polyline", measurementModeName.polyline)
      .set("tipDefect", measurementModeName.tipDefect);

    return httpParams;
  }
}
